import type { MemberSummary } from './usageEventAnalysis';

const SPARK_HEIGHT = 24;
const SPARK_BAR_GAP = 2;

function relativeTimeShort(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'now';
}

function Sparkline({ daily, flaggedDays }: { daily: number[]; flaggedDays: boolean[] }) {
  const max = Math.max(1, ...daily);
  const barCount = daily.length;
  const totalGap = SPARK_BAR_GAP * (barCount - 1);
  const barWidth = (100 - totalGap) / barCount;

  return (
    <svg
      role="img"
      aria-label={`14 days: ${daily.join(', ')} events`}
      viewBox={`0 0 100 ${SPARK_HEIGHT}`}
      preserveAspectRatio="none"
      className="w-full h-4 my-1.5"
    >
      {daily.map((count, i) => {
        const h = (count / max) * SPARK_HEIGHT;
        const x = i * (barWidth + SPARK_BAR_GAP);
        const y = SPARK_HEIGHT - h;
        const isFlagged = flaggedDays[i];
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={h || 1}
            style={{ fill: isFlagged ? '#ef4444' : '#6366f1' }}
            opacity={count === 0 ? 0.25 : 0.85}
          />
        );
      })}
    </svg>
  );
}

export function MemberActivityStrip({ members }: { members: MemberSummary[] }) {
  if (members.length === 0) {
    return (
      <section aria-label="Family activity" className="px-6 py-3">
        <h2 className="text-xs uppercase tracking-wide text-hbs-text-mute mb-2">
          Family activity — last 14 days
        </h2>
        <div className="text-sm text-hbs-text-mute">No activity in the last 30 days.</div>
      </section>
    );
  }

  return (
    <section aria-label="Family activity" className="px-6 py-3">
      <h2 className="text-xs uppercase tracking-wide text-hbs-text-mute mb-2">
        Family activity — last 14 days
      </h2>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {members.map((m) => (
          <article
            key={m.userId}
            className="bg-hbs-surface border border-hbs-border rounded-hbs-md p-3"
          >
            <div className="text-sm font-medium text-hbs-text truncate">{m.email}</div>
            <Sparkline daily={m.daily} flaggedDays={m.flaggedDays} />
            <div className="text-xs text-hbs-text-mute">
              {m.weekEvents} {m.weekEvents === 1 ? 'event' : 'events'} this week · {relativeTimeShort(m.lastEventAt)}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
